import { useTheme } from '@mui/material';
import React from 'react';

/**
 * Armhr logo — outer + inner chevron mark, "armhr" wordmark.
 *
 * Uses the refreshed brand geometry. Four palette variants:
 *
 * - `executive` (default) — Trust + Authority chevrons, Executive wordmark.
 *   Used by `@armhr/hcm` and any light-surface consumer.
 * - `executive-on-dark` — Trust + Authority chevrons (same as `executive`)
 *   but with a Warm White wordmark for placement on the Executive navy
 *   sidebar. Used by `@armhr/hcm`.
 * - `trust` — entirely Trust soft blue. Used by `@armhr/quote` (which has
 *   a navy sidebar where Authority would clash).
 * - `white` — entirely white. Used by `@armhr/ops` for the dark sidebar.
 *
 * Source SVGs live in `packages/ui/logo/` for design reference; the
 * geometry is embedded inline here so the component renders without an
 * asset request.
 *
 * Legacy props (`color`, `textColor`) still work for callers that haven't
 * adopted `variant` yet. When `color` is set, all three paths use it;
 * when only `textColor` is set, the wordmark uses it and chevrons fall
 * back to brand defaults.
 */

type LogoVariant = 'executive' | 'executive-on-dark' | 'trust' | 'white';

interface LogoColors {
  outer: string;
  inner: string;
  text: string;
}

const VARIANT_COLORS: Record<LogoVariant, LogoColors> = {
  executive: { outer: '#7BA4D0', inner: '#2E5F99', text: '#0D2440' },
  'executive-on-dark': { outer: '#7BA4D0', inner: '#2E5F99', text: '#FAFAF7' },
  trust: { outer: '#7BA4D0', inner: '#7BA4D0', text: '#7BA4D0' },
  white: { outer: '#FFFFFF', inner: '#FFFFFF', text: '#FFFFFF' },
};

const FULL_VIEWBOX = '0 0 649 116';
const MARK_VIEWBOX = '567 0 82 65';

const Logo: React.FC<{
  /** Color variant. Defaults to executive (HCM / light surfaces). */
  variant?: LogoVariant;
  style?: any;
  /** Legacy: overrides text/wordmark color when no `variant` is set. */
  textColor?: string;
  /** Show the wordmark + chevrons; when false, only the chevrons render. */
  showText?: boolean;
  /** Explicit pixel height. When omitted, derived from `width` or default. */
  height?: number;
  /**
   * Pixel size. Acts as the desired *height* when used alone — the rendered
   * SVG width is then computed from the brand's intrinsic ratio (~5.59:1
   * with wordmark, 1.26:1 mark-only). When `height` is also provided, both
   * are honored literally.
   */
  width?: number;
  /** Legacy: when set, applied to all three paths. */
  color?: string;
}> = ({ variant, style, width, height, showText = true, textColor, color }) => {
  const theme = useTheme();

  let palette: LogoColors;
  if (variant) {
    palette = VARIANT_COLORS[variant];
  } else if (color) {
    palette = { outer: color, inner: color, text: textColor || color };
  } else {
    const defaults = VARIANT_COLORS.executive;
    palette = {
      outer: defaults.outer,
      inner: defaults.inner,
      text:
        textColor ??
        (theme.palette.mode === 'dark'
          ? theme.palette.common.white
          : defaults.text),
    };
  }

  // Intrinsic ratio of the active viewBox.
  const intrinsicRatio = showText ? 649 / 116 : 82 / 65;

  // Sizing model:
  //   - both `width` and `height` given → literal (callers know what they
  //     want, even if it squishes the mark).
  //   - only `width` given → treat as the desired *height*. This matches
  //     the legacy API where `width=34` produced a roughly 34-tall logo,
  //     and is the natural "how big is this thing in my row" interpretation.
  //   - only `height` given → use it; derive width.
  //   - neither → default 32px tall.
  const targetHeight = height ?? width ?? 32;
  const renderHeight = targetHeight;
  const renderWidth =
    width !== undefined && height !== undefined
      ? width
      : targetHeight * intrinsicRatio;

  return (
    <svg
      width={renderWidth}
      height={renderHeight}
      viewBox={showText ? FULL_VIEWBOX : MARK_VIEWBOX}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      preserveAspectRatio="xMinYMid meet"
      style={{
        ...style,
        minWidth: renderWidth,
        width: renderWidth,
        height: renderHeight,
        flexShrink: 0,
      }}
      role="img"
      aria-label="Armhr"
    >
      <path
        d="M567.042 0.119971C573.028 -0.0600289 579.544 0.0506013 585.572 0.0520075L621.019 32.5889L585.788 64.8408C580.106 65.0662 573.052 64.8759 567.267 64.8717L602.602 32.5265C590.808 22.2098 578.742 10.74 567.042 0.119971Z"
        fill={palette.outer}
      />
      <path
        d="M594.136 0.146217C600.075 -0.102689 606.642 0.0398146 612.628 0.0477834L648.028 32.5645C637.378 42.937 623.939 54.8086 612.853 64.8403C607.191 65.0756 599.991 64.8773 594.211 64.8778L629.498 32.5673L594.136 0.146217Z"
        fill={palette.inner}
      />
      {showText && (
        <path
          d="M70.1875 94.25H31.4375L25.6875 106.25C24.8958 107.917 24.5 109.229 24.5 110.188C24.5 112.021 25.8958 113.292 28.6875 114V116H0.4375V114C2.10417 113.667 3.375 113.083 4.25 112.25C5.125 111.417 6.125 109.792 7.25 107.375L38.625 41.8125C39.625 39.7292 40.125 38.0625 40.125 36.8125C40.125 34.8958 38.9167 33.5208 36.5 32.6875V30.6875H66.75V32.6875C64.2917 33.3958 63.0625 34.625 63.0625 36.375C63.0625 37.5833 63.4583 38.9583 64.25 40.5L96.75 106.25C98.125 109.042 99.3333 110.958 100.375 112C101.417 113 102.812 113.667 104.562 114V116H73.1875V114C75.9375 113.542 77.3125 112.25 77.3125 110.125C77.3125 109.25 76.8958 107.958 76.0625 106.25L70.1875 94.25ZM64.8125 82.5625L51 52.1875L37 82.5625H64.8125ZM138.789 81.1875V109.5C138.789 110.75 139.081 111.729 139.664 112.438C140.289 113.104 141.331 113.625 142.789 114V116H116.164V114C117.664 113.583 118.706 113.042 119.289 112.375C119.873 111.708 120.164 110.75 120.164 109.5V37.25C120.164 35.9583 119.873 34.9792 119.289 34.3125C118.706 33.6458 117.664 33.1042 116.164 32.6875V30.6875H169.539C176.039 30.6875 181.185 31.0625 184.977 31.8125C188.81 32.5625 192.144 33.875 194.977 35.75C198.269 37.9167 200.727 40.8542 202.352 44.5625C203.894 48.1458 204.664 51.9792 204.664 56.0625C204.664 69.9792 196.935 78.125 181.477 80.5L198.039 104.062C200.956 108.188 203.081 110.854 204.414 112.062C205.789 113.229 207.456 113.875 209.414 114V116H178.539V114C180.664 113.667 181.727 112.917 181.727 111.75C181.727 111 180.748 109.188 178.789 106.312L161.539 81.1875H138.789ZM138.789 67.125H167.539C171.789 67.125 174.789 67 176.539 66.75C178.289 66.5 179.894 65.9792 181.352 65.1875C184.519 63.4792 186.102 60.3958 186.102 55.9375C186.102 51.4792 184.519 48.3958 181.352 46.6875C179.852 45.8542 178.248 45.3333 176.539 45.125C174.873 44.875 171.873 44.75 167.539 44.75H138.789V67.125ZM276.516 114.812L245.079 57.0625V109.438C245.079 110.729 245.371 111.708 245.954 112.375C246.537 113.042 247.579 113.583 249.079 114V116H224.016V114C225.516 113.583 226.558 113.042 227.141 112.375C227.725 111.708 228.016 110.729 228.016 109.438V37.1875C228.016 35.9375 227.725 34.9792 227.141 34.3125C226.558 33.6458 225.516 33.1042 224.016 32.6875V30.6875H256.891V32.6875C254.933 33.1875 253.954 34.3333 253.954 36.125C253.954 36.9583 254.246 37.9167 254.829 39L278.079 81.875L301.641 39.75C302.683 37.8333 303.204 36.4375 303.204 35.5625C303.204 33.8542 302.162 32.8958 300.079 32.6875V30.6875H332.329V32.6875C330.829 33.1042 329.787 33.6458 329.204 34.3125C328.621 34.9792 328.329 35.9375 328.329 37.1875V109.438C328.329 110.729 328.621 111.708 329.204 112.375C329.787 113.042 330.829 113.583 332.329 114V116H305.704V114C307.162 113.625 308.183 113.104 308.766 112.438C309.391 111.729 309.704 110.729 309.704 109.438V57.0625L276.516 114.812ZM423.931 78.75H374.743V109.5C374.743 110.75 375.035 111.729 375.618 112.438C376.243 113.104 377.285 113.625 378.743 114V116H352.118V114C353.618 113.583 354.66 113.042 355.243 112.375C355.827 111.708 356.118 110.75 356.118 109.5V37.1875C356.118 35.9375 355.827 34.9792 355.243 34.3125C354.66 33.6458 353.618 33.1042 352.118 32.6875V30.6875H378.743V32.6875C377.243 33.0625 376.202 33.6042 375.618 34.3125C375.035 34.9792 374.743 35.9375 374.743 37.1875V64.6875H423.931V37.1875C423.931 35.9375 423.639 34.9792 423.056 34.3125C422.473 33.6042 421.431 33.0625 419.931 32.6875V30.6875H446.556V32.6875C445.056 33.1042 444.014 33.6458 443.431 34.3125C442.848 34.9792 442.556 35.9375 442.556 37.1875V109.5C442.556 110.75 442.848 111.708 443.431 112.375C444.014 113.042 445.056 113.583 446.556 114V116H419.931V114C421.389 113.625 422.41 113.104 422.993 112.438C423.618 111.729 423.931 110.75 423.931 109.5V78.75ZM488.97 81.1875V109.5C488.97 110.75 489.262 111.729 489.845 112.438C490.47 113.104 491.512 113.625 492.97 114V116H466.345V114C467.845 113.583 468.887 113.042 469.47 112.375C470.054 111.708 470.345 110.75 470.345 109.5V37.25C470.345 35.9583 470.054 34.9792 469.47 34.3125C468.887 33.6458 467.845 33.1042 466.345 32.6875V30.6875H519.72C526.22 30.6875 531.366 31.0625 535.158 31.8125C538.991 32.5625 542.325 33.875 545.158 35.75C548.45 37.9167 550.908 40.8542 552.533 44.5625C554.075 48.1458 554.845 51.9792 554.845 56.0625C554.845 69.9792 547.116 78.125 531.658 80.5L548.22 104.062C551.137 108.188 553.262 110.854 554.595 112.062C555.97 113.229 557.637 113.875 559.595 114V116H528.72V114C530.845 113.667 531.908 112.917 531.908 111.75C531.908 111 530.929 109.188 528.97 106.312L511.72 81.1875H488.97ZM488.97 67.125H517.72C521.97 67.125 524.97 67 526.72 66.75C528.47 66.5 530.075 65.9792 531.533 65.1875C534.7 63.4792 536.283 60.3958 536.283 55.9375C536.283 51.4792 534.7 48.3958 531.533 46.6875C530.033 45.8542 528.429 45.3333 526.72 45.125C525.054 44.875 522.054 44.75 517.72 44.75H488.97V67.125Z"
          fill={palette.text}
        />
      )}
    </svg>
  );
};

export default Logo;
